/**
 * HUD 状态栏组件
 * 参考 claude-hud 设计，显示实时状态信息
 */
import type { WSHUDStatusMessage, HUDActivity, ContainerStatusInfo } from '../api/types.js';

export type HUDStatus = WSHUDStatusMessage;

export class StatusBar {
  private element: HTMLElement;
  private modelEl: HTMLElement;
  private contextBarEl: HTMLElement;
  private contextFillEl: HTMLElement;
  private contextValueEl: HTMLElement;
  private containerEl: HTMLElement;
  private containerIndicatorEl: HTMLElement;
  private containerTextEl: HTMLElement;
  private activityLineEl: HTMLElement;
  private activityTextEl: HTMLElement;

  constructor() {
    // 获取 DOM 元素
    this.element = document.getElementById('hud-status-bar')!;
    this.modelEl = document.getElementById('hud-model')!;
    this.contextBarEl = document.getElementById('hud-context-bar')!;
    this.contextFillEl = document.getElementById('hud-context-fill')!;
    this.contextValueEl = document.getElementById('hud-context-value')!;
    this.containerEl = document.getElementById('hud-container')!;
    this.containerIndicatorEl = this.containerEl.querySelector('.hud-container-indicator') as HTMLElement;
    this.containerTextEl = this.containerEl.querySelector('.hud-container-text') as HTMLElement;
    this.activityLineEl = document.getElementById('hud-activity-line')!;
    this.activityTextEl = document.getElementById('hud-activity-text')!;
  }

  /**
   * 更新 HUD 状态
   */
  update(status: HUDStatus): void {
    // 更新模型
    if (status.model) {
      this.modelEl.textContent = status.model;
    }

    // 更新上下文使用情况
    if (status.contextUsage !== undefined) {
      this.updateContext(status.contextUsage, status.contextTokens, status.contextMax);
    }

    // 更新容器状态（优先使用详细列表）
    if (status.containers && status.containers.length > 0) {
      this.updateMultipleContainers(status.containers);
    } else if (status.containerStatus) {
      this.updateContainerStatus(status.containerStatus, status.containerCount);
    }

    // 更新活动状态
    if (status.activity) {
      this.updateActivity(status.activity);
    } else {
      this.activityLineEl.style.display = 'none';
    }
  }

  /**
   * 更新多个容器状态
   */
  private updateMultipleContainers(containers: ContainerStatusInfo[]): void {
    if (containers.length === 0) {
      this.containerTextEl.textContent = '容器空闲';
      return;
    }

    // 显示容器列表
    const statusTexts = containers.map((c) => {
      const statusIcon = c.status === 'running' ? '◐' : (c.status === 'idle' ? '○' : '●');
      return `${statusIcon} ${c.id}`;
    });

    this.containerTextEl.textContent = statusTexts.join(' | ');
    this.containerIndicatorEl.classList.remove('running', 'error');

    // 如果有正在运行的容器，显示运行指示器
    const hasRunning = containers.some((c) => c.status === 'running');
    if (hasRunning) {
      this.containerIndicatorEl.classList.add('running');
    }
  }

  /**
   * 更新上下文使用情况
   */
  private updateContext(usage: number, tokens?: number, max?: number): void {
    // 设置进度条宽度
    this.contextFillEl.style.width = `${usage}%`;

    // 设置颜色（绿色 -> 黄色 -> 红色）
    this.contextFillEl.classList.remove('warning', 'critical');
    if (usage >= 85) {
      this.contextFillEl.classList.add('critical');
    } else if (usage >= 70) {
      this.contextFillEl.classList.add('warning');
    }

    // 设置显示值
    if (tokens !== undefined && max !== undefined) {
      // 显示 token 数量
      const tokensK = Math.round(tokens / 1000);
      const maxK = Math.round(max / 1000);
      this.contextValueEl.textContent = `${tokensK}k/${maxK}k`;
    } else {
      // 显示百分比
      this.contextValueEl.textContent = `${usage}%`;
    }
  }

  /**
   * 更新容器状态
   */
  private updateContainerStatus(status: 'ready' | 'running' | 'error', containerCount?: number): void {
    this.containerIndicatorEl.classList.remove('running', 'error');

    // 显示容器数量（如果有）
    const countText = containerCount !== undefined ? ` (${containerCount})` : '';

    switch (status) {
      case 'ready':
        this.containerTextEl.textContent = `容器就绪${countText}`;
        break;
      case 'running':
        this.containerIndicatorEl.classList.add('running');
        this.containerTextEl.textContent = `处理中${countText}`;
        break;
      case 'error':
        this.containerIndicatorEl.classList.add('error');
        this.containerTextEl.textContent = `容器错误${countText}`;
        break;
    }
  }

  /**
   * 更新活动状态
   */
  private updateActivity(activity: NonNullable<HUDStatus['activity']>): void {
    if (!activity.text) {
      this.activityLineEl.style.display = 'none';
      return;
    }

    this.activityLineEl.style.display = 'flex';

    // 根据类型设置图标
    const icon = this.activityLineEl.querySelector('.hud-activity-icon') as HTMLElement;
    switch (activity.type) {
      case 'tool':
        icon.textContent = '◐';
        break;
      case 'agent':
        icon.textContent = '◈';
        break;
      case 'task':
        icon.textContent = '▸';
        break;
      default:
        icon.textContent = '◐';
    }

    // 设置活动文本
    this.activityTextEl.textContent = activity.text;

    // 如果有进度，可以添加进度条（未来扩展）
    if (activity.progress !== undefined) {
      this.activityTextEl.textContent = `${activity.text} (${activity.progress}%)`;
    }
  }

  /**
   * 显示错误状态
   */
  showError(message: string): void {
    this.updateContainerStatus('error');
    this.updateActivity({
      type: 'task',
      text: message,
    });
  }

  /**
   * 重置为默认状态
   */
  reset(): void {
    this.modelEl.textContent = '-';
    this.contextFillEl.style.width = '0%';
    this.contextFillEl.classList.remove('warning', 'critical');
    this.contextValueEl.textContent = '-';
    this.updateContainerStatus('ready');
    this.activityLineEl.style.display = 'none';
  }
}
